<template>
	<CSettingsSection
		:loading="loading"
		title="Compliance"
		class="mt-5"
	>
		<div class="b9:flex gap-x-4 b10:gap-x-5 b12:gap-x-6 my-4">
			<div class="flex-1 mb-4">
				<h3 class="sz-2 mb-2">HIPAA Compliance</h3>
				<p>Business Associate Agreement? <em>{{ isHipaaCompliant ? 'Yes' : 'No' }}</em></p>
				<a v-if="!isHipaaCompliant" id="sign-baa" href="" @click.prevent="showHipaaForm = true">
					Sign our BAA to get started.
				</a>
				<template v-if="isHipaaCompliant">
					<a v-if="baaLink" id="view-baa" :href="baaLink" target="_blank" class="block">
						View your BAA
					</a>
					<a id="exit-baa" href="" class="block" @click.prevent="showExitHipaaDialog = true">
						Exit your BAA
					</a>
				</template>
			</div>
			<div class="flex-1">
				<h3 class="sz-2 mb-2">Privacy Regulation Compliance</h3>
				<p>Data Processing Addendum? <em>{{ isGdprCompliant ? 'Yes' : 'No' }}</em></p>
				<a v-if="!isGdprCompliant" id="sign-dpa" href="" @click.prevent="showGdprForm = true">
					Sign our DPA to get started.
				</a>
				<template v-if="isGdprCompliant">
					<a id="view-dpa" :href="dpaLink" target="_blank" class="block">
						View your DPA
					</a>
					<a id="exit-dpa" href="" class="block" @click.prevent="showExitGdprDialog = true">
						Exit your DPA
					</a>
				</template>
			</div>
		</div>
		<!-- Dialog to handle hipaa form & error cases -->
		<CSignAgreementDialog
			v-if="planName === 'Enterprise' && !isTrial"
			:visible="showHipaaForm"
			:prefill-data="prefillData"
			form-id="26"
			allow-confirm
			:confirmation-options="hipaaConfirmationOptions"
			title="HIPAA Business Associate Agreement"
			@close="handleClose"
			@after-submit="submitHipaa"
		/>
		<!-- Dialog to handle insufficient plan level -->
		<CHipaaUnavailableDialog
			v-else
			:visible="showHipaaForm"
			:is-trial="isTrial"
			:org-name="prefillData.User.Organization"
			:plan-level="planName"
			@close="showHipaaForm = false"
		/>
		<!-- Dialog to exit hipaa agreement -->
		<CExitAgreementDialog
			:visible="showExitHipaaDialog"
			title="Exit HIPAA Agreement"
			@close="showExitHipaaDialog = false"
			@handle-exit="handleExitBaa"
		>
			<template #content>
				<p>
					By continuing, you will be cancelling your business associate agreement with Cognito, LLC.<br><br>
					Are you sure you want to continue?
				</p>
			</template>
		</CExitAgreementDialog><!-- Dialog to handle gdpr form -->
		<CSignAgreementDialog
			:visible="showGdprForm"
			:prefill-data="prefillData"
			form-id="112"
			title="Data Processing Addendum"
			@close="showGdprForm = false"
			@after-submit="submitGdpr"
		/>
		<!-- Dialog to exit gdpr agreement -->
		<CExitAgreementDialog
			:visible="showExitGdprDialog"
			title="Exit Data Processing Addendum"
			@close="showExitGdprDialog = false"
			@handle-exit="handleExitDpa"
		>
			<template #content>
				<p>
					By continuing, you will be cancelling your Data Processing Addendum with Cognito, LLC.<br><br>
					Are you sure you want to continue?
				</p>
			</template>
		</CExitAgreementDialog>
	</CSettingsSection>
</template>

<script lang="ts">
	import { defineComponent, ref, computed } from 'vue';
	import CSettingsSection from './SettingsSection.vue';
	import CHipaaUnavailableDialog from './HipaaUnavailableDialog.vue';
	import CSignAgreementDialog from './SignAgreementDialog.vue';
	import { changeHipaaStatus, changeGdprStatus, getGdprStatus, getHipaaStatus } from '@cognitoforms/api/services/settings/organization-settings-service';
	import type { Prefill } from '@cognitoforms/api/services/settings/organization-settings-service';
	import CExitAgreementDialog from './ExitAgreementDialog.vue';
	import { globalStore } from 'src/stores/global-store';

	export default defineComponent({
		components: {
			CSettingsSection,
			CHipaaUnavailableDialog,
			CSignAgreementDialog,
			CExitAgreementDialog
		},
		props: {
			isHipaaCompliant: { type: Boolean, default: false }
		},
		emits: ['hipaa-compliant-status-update', 'update-email-domains'],
		setup() {
			return {
				loading: ref(true),
				emitUpdateOnClose: ref(false),
				baaLink: ref(''),
				isGdprCompliant: ref(true),
				dpaLink: ref(''),
				showHipaaForm: ref(false),
				showGdprForm: ref(false),
				showExitHipaaDialog: ref(false),
				showExitGdprDialog: ref(false),
				globalStore,
				prefillData: computed((): Prefill => {
					if (!globalStore.user || !globalStore.organization)
						return {
							User: {
								Name: { First: '', Last: '' },
								Email: '',
								UserId: '',
								Organization: '',
								OrganizationCode: '',
								OrganizationId: ''
							}
						};
					return {
						User: {
							Name: { First: globalStore.user.FirstName, Last: globalStore.user.LastName },
							Email: globalStore.user.Email,
							UserId: globalStore.user.Id,
							Organization: globalStore.organization.Name,
							OrganizationCode: globalStore.organization.Code,
							OrganizationId: globalStore.organization.Id
						}
					};
				}),
				isTrial: computed(() => {
					return globalStore.organization?.TrialDaysRemaining > 0;
				}),
				planName: computed(() => {
					if (!globalStore.organization?.Plan)
						return '';
					return globalStore.organization.Plan.Name;
				}),
				hipaaConfirmationOptions: {
					title: 'Update in progress',
					message: 'We\'re processing your change. This may take a few more moments',
					showClose: true,
					customClass: 'notification notification--confirmation-dialog',
					showConfirmButton: false,
					cancelButtonText: 'Okay',
					cancelButtonClass: 'button button--secondary sz-3'
				}
			};
		},
		watch: {
			async 'globalStore.organization.IsHipaaCompliant'() {
				const hipaaSettings = await getHipaaStatus();
				this.baaLink = hipaaSettings.AgreementLink;
			}
		},
		async mounted() {
			const hipaaSettings = await getHipaaStatus();
			this.baaLink = hipaaSettings.AgreementLink;

			const gdprSettings = await getGdprStatus();
			this.isGdprCompliant = gdprSettings.IsCompliant;
			this.dpaLink = gdprSettings.AgreementLink;

			this.loading = false;
		},
		methods: {
			async submitHipaa(e) {
				this.emitUpdateOnClose = true;
				try {
					// assume changeHipaaStatus will complete with no errors
					this.baaLink = e.data.documents[0].link;
					await changeHipaaStatus(true, e.data.documents[0].link);
				}
				catch {
					// reset everything if it doesn't
					this.showHipaaForm = false;
					this.baaLink = '';
					this.emitUpdateOnClose = false;
					this.$emit('hipaa-compliant-status-update', false, true);
				}
				this.$emit('update-email-domains');
			},
			handleClose() {
				if (this.emitUpdateOnClose) // this is only true if the user has successfully signed the BAA
					this.$emit('hipaa-compliant-status-update', true, false);
				this.showHipaaForm = false;
				this.emitUpdateOnClose = false;
			},
			async submitGdpr(e) {
				try {
					await changeGdprStatus(true, e.data.documents[0].link);
					this.isGdprCompliant = true;
					this.dpaLink = e.data.documents[0].link;
				}
				catch {
					this.showGdprForm = false;
				}
			},
			async handleExitBaa() {
				try {
					await changeHipaaStatus(false);
					this.baaLink = '';
					this.$emit('hipaa-compliant-status-update', false, true);
				}
				catch { }
				this.showExitHipaaDialog = false;
			},
			async handleExitDpa() {
				try {
					await changeGdprStatus(false);
					this.isGdprCompliant = false;
					this.dpaLink = '';
				}
				catch { }
				this.showExitGdprDialog = false;
			}
		}
	});
</script>